"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { auth } from "@/lib/firebase";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendPasswordResetEmail
} from "firebase/auth";
import { IconBrandGoogle, IconLoader2 } from "@tabler/icons-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import AuthLayout from "./AuthLayout";
import { logLogin } from "@/hooks/use-analytics";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";

const signInSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
  password: z
    .string()
    .min(6, { message: "Password must be at least 6 characters" })
});

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Invalid email address" })
});

type SignInFormValues = z.infer<typeof signInSchema>;
type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export default function SignInViewPage() {
  const router = useRouter();
  const { currentUser, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);

  // Check for stored loading state on component mount (for mobile redirects)
  useEffect(() => {
    const storedLoading = sessionStorage.getItem("googleAuthLoading");
    if (storedLoading === "true") {
      setGoogleLoading(true);
    }
  }, []);

  useEffect(() => {
    console.log("Auth state check:", {
      authLoading,
      currentUser: currentUser?.email ?? null
    });
    if (!authLoading && currentUser) {
      console.log("Auth state confirmed user logged in, redirecting...");
      // Clear any pending Google auth state
      if (sessionStorage.getItem("googleAuthLoading") === "true") {
        console.log("Clearing Google auth loading state on successful login");
        sessionStorage.removeItem("googleAuthLoading");
        setGoogleLoading(false);
        toast.success("Signed in with Google successfully!");
      }
      router.push("/dashboard");
    } else if (!authLoading && !currentUser) {
      // Reset loading states when user is signed out (but not if Google auth is in progress)
      const googleAuthInProgress =
        sessionStorage.getItem("googleAuthLoading") === "true";
      if (!googleAuthInProgress) {
        setGoogleLoading(false);
        setLoading(false);
        sessionStorage.removeItem("googleAuthLoading");
      }
    }
  }, [currentUser, authLoading, router]);

  // Handle Google redirect result for mobile
  useEffect(() => {
    const handleRedirectResult = async () => {
      // Check if we were in the middle of a Google auth flow
      const wasGoogleAuthInProgress =
        sessionStorage.getItem("googleAuthLoading") === "true";

      if (wasGoogleAuthInProgress) {
        console.log(
          "Detected Google auth was in progress, checking for redirect result..."
        );
        setGoogleLoading(true);

        try {
          const result = await getRedirectResult(auth);
          console.log("Redirect result:", result);

          if (result) {
            console.log("User signed in via redirect:", result.user);
            // Clear stored loading state
            sessionStorage.removeItem("googleAuthLoading");
            // User successfully signed in with redirect
            toast.success("Signed in with Google successfully!");
            setGoogleLoading(false);
            // Log analytics event
            void logLogin("google");

            // Don't navigate here - let the auth state change effect handle it
            console.log(
              "Redirect result processed, waiting for auth state update..."
            );
          } else {
            console.log(
              "No redirect result found, but auth might still be in progress..."
            );
            // Wait a bit longer and check auth state
            setTimeout(() => {
              if (!currentUser) {
                console.log(
                  "Still no user after timeout, clearing loading state"
                );
                sessionStorage.removeItem("googleAuthLoading");
                setGoogleLoading(false);
              }
            }, 2000);
          }
        } catch (error: any) {
          console.error("Google redirect error:", error);
          // Clear stored loading state on error
          sessionStorage.removeItem("googleAuthLoading");
          setGoogleLoading(false);
          if (error.code === "auth/account-exists-with-different-credential") {
            toast.error(
              "An account already exists with this email address using a different sign-in method."
            );
          } else {
            toast.error(error.message ?? "Failed to sign in with Google.");
          }
        }
      }
    };

    // Only run this on mount, not on every render
    handleRedirectResult();
  }, [currentUser]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors
  } = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema)
  });

  const forgotPasswordForm = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema)
  });

  const onEmailSubmit: SubmitHandler<SignInFormValues> = async (data) => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, data.email, data.password);
      toast.success("Signed in successfully!");
    } catch (error: any) {
      if (error.code === "auth/invalid-credential") {
        toast.error("Invalid email or password. Please try again.");
      } else {
        toast.error(
          error.message ?? "Failed to sign in. Please check your credentials."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const onForgotPasswordSubmit: SubmitHandler<
    ForgotPasswordFormValues
  > = async (data) => {
    setForgotPasswordLoading(true);
    try {
      await sendPasswordResetEmail(auth, data.email);
      toast.success("Password reset email sent. Please check your inbox.");
      setIsForgotPasswordOpen(false); // Close the dialog
      clearErrors(); // Clear errors on the main sign-in form
    } catch (error: any) {
      toast.error(error.message ?? "Failed to send password reset email.");
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Check if device is mobile to use redirect instead of popup
  const isMobile = () => {
    if (typeof window === "undefined") return false;

    // Primary detection: user agent strings for mobile devices
    const mobileUserAgents =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    // Secondary detection: touch capability combined with small screen
    // This helps catch edge cases but only when combined with touch
    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth < 768;

    // Force redirect for mobile browsers (more reliable than popup)
    return mobileUserAgents || (isTouchDevice && isSmallScreen);
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    const provider = new GoogleAuthProvider();

    // Add custom parameters to ensure proper mobile handling
    provider.setCustomParameters({
      prompt: "select_account"
    });

    try {
      // Always try popup first, with fallback to redirect on mobile if popup fails
      try {
        console.log("Attempting Google sign-in with popup...");
        await signInWithPopup(auth, provider);
        toast.success("Signed in with Google successfully!");
        setGoogleLoading(false);
      } catch (popupError: any) {
        console.log("Popup failed, error:", popupError.code);

        // If popup fails on mobile, fall back to redirect
        if (
          isMobile() &&
          (popupError.code === "auth/popup-blocked" ||
            popupError.code === "auth/popup-closed-by-user" ||
            popupError.code === "auth/cancelled-popup-request")
        ) {
          console.log("Falling back to redirect for mobile...");
          // Store loading state in sessionStorage for mobile redirect
          sessionStorage.setItem("googleAuthLoading", "true");
          // Use redirect as fallback for mobile devices
          await signInWithRedirect(auth, provider);
          // Note: success will be handled by redirect result effect
        } else {
          // Re-throw other errors
          throw popupError;
        }
      }
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      // Clear loading state on error
      sessionStorage.removeItem("googleAuthLoading");
      setGoogleLoading(false);

      if (error.code === "auth/popup-closed-by-user") {
        toast.info("Google Sign-in was cancelled.");
      } else if (
        error.code === "auth/account-exists-with-different-credential"
      ) {
        toast.error(
          "An account already exists with this email address using a different sign-in method."
        );
      } else if (error.code === "auth/popup-blocked") {
        toast.error(
          "Popup was blocked. Please allow popups for this site and try again."
        );
      } else {
        toast.error(error.message ?? "Failed to sign in with Google.");
      }
    }
  };

  if (authLoading || (!authLoading && currentUser)) {
    return (
      <div className='bg-background/80 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm'>
        <div className='flex flex-col items-center space-y-4'>
          <IconLoader2 className='text-primary h-10 w-10 animate-spin' />
          <p className='text-muted-foreground text-sm'>
            {googleLoading ? "Signing in with Google..." : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <AuthLayout
      title='Sign In'
      description='Enter your email and password below to sign in'
    >
      <form onSubmit={handleSubmit(onEmailSubmit)} className='w-full space-y-4'>
        <div>
          <Label htmlFor='email'>Email</Label>
          <Input
            id='email'
            type='email'
            placeholder='m@example.com'
            {...register("email")}
          />
          {errors.email && (
            <p className='text-destructive text-sm'>{errors.email.message}</p>
          )}
        </div>
        <div>
          <div className='flex items-center justify-between'>
            <Label htmlFor='password'>Password</Label>
            <Dialog
              open={isForgotPasswordOpen}
              onOpenChange={setIsForgotPasswordOpen}
            >
              <DialogTrigger asChild>
                <Button
                  variant='link'
                  className='text-primary h-auto p-0 text-sm font-medium hover:underline'
                >
                  Forgot password?
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Forgot Password</DialogTitle>
                  <DialogDescription>
                    Enter your email address below and we&apos;ll send you a
                    link to reset your password.
                  </DialogDescription>
                </DialogHeader>
                <form
                  onSubmit={forgotPasswordForm.handleSubmit(
                    onForgotPasswordSubmit
                  )}
                >
                  <div className='space-y-2'>
                    <Label htmlFor='forgot-password-email'>Email</Label>
                    <Input
                      id='forgot-password-email'
                      type='email'
                      placeholder='m@example.com'
                      {...forgotPasswordForm.register("email")}
                    />
                    {forgotPasswordForm.formState.errors.email && (
                      <p className='text-destructive text-sm'>
                        {forgotPasswordForm.formState.errors.email.message}
                      </p>
                    )}
                  </div>
                  <DialogFooter className='mt-4'>
                    <DialogClose asChild>
                      <Button type='button' variant='ghost'>
                        Cancel
                      </Button>
                    </DialogClose>
                    <Button type='submit' disabled={forgotPasswordLoading}>
                      {forgotPasswordLoading ? "Sending..." : "Send Reset Link"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <Input id='password' type='password' {...register("password")} />
          {errors.password && (
            <p className='text-destructive text-sm'>
              {errors.password.message}
            </p>
          )}
        </div>
        <Button
          type='submit'
          className='w-full'
          disabled={loading || googleLoading}
        >
          {loading ? "Signing In..." : "Sign In with Email"}
        </Button>
      </form>

      <div className='relative w-full'>
        <div className='absolute inset-0 flex items-center'>
          <span className='w-full border-t' />
        </div>
        <div className='relative flex justify-center text-xs uppercase'>
          <span className='bg-background text-muted-foreground px-2'>
            Or continue with
          </span>
        </div>
      </div>

      <Button
        variant='outline'
        className='w-full'
        onClick={handleGoogleSignIn}
        disabled={loading || googleLoading}
      >
        {googleLoading ? (
          <IconLoader2 className='mr-2 h-4 w-4 animate-spin' />
        ) : (
          <IconBrandGoogle className='mr-2 h-4 w-4' />
        )}
        Sign In with Google
      </Button>

      <p className='text-muted-foreground px-8 text-center text-sm'>
        Don&apos;t have an account?{" "}
        <Link
          href='/auth/sign-up'
          className='hover:text-primary underline underline-offset-4'
        >
          Sign Up
        </Link>
      </p>

      <p className='text-muted-foreground px-8 text-center text-sm'>
        By clicking continue, you agree to our{" "}
        <Link
          href='/terms'
          className='hover:text-primary underline underline-offset-4'
        >
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link
          href='/privacy'
          className='hover:text-primary underline underline-offset-4'
        >
          Privacy Policy
        </Link>
        .
      </p>
    </AuthLayout>
  );
}
